// react
import { useCallback, useEffect, useState } from 'react';

// types
import { Account } from '../@types';

// services
import { AccountQueryParams, fetchAccounts, ApiResponse, FetchParams } from '../services/api';

// Types

export interface UseAccountsParams {
	isEnabled?: boolean;
	projectId?: number;
	query?: AccountQueryParams;
}

export interface UseAccountsReturn {
	data: Account[];
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<Account[]>['pagination'];
	refetch: () => Promise<void>;
}

// Hook

export const useAccounts = ({
	isEnabled = true,
	projectId,
	query = {},
}: UseAccountsParams = {}): UseAccountsReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<Account[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState<ApiResponse<Account[]>['pagination']>(undefined);

	const queryKey = JSON.stringify(query);

	// fetch data
	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const params: FetchParams = { accessToken, idToken };
			const res = await fetchAccounts(params, { ...query, project: projectId });

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch accounts');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// refetch function
	const refetch = useCallback(async () => {
		await fetchData();
	}, [fetchData]);

	return {
		data,
		error,
		loading,
		pagination,
		refetch,
	};
};
